import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  Badge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Alert,
  EmptyState,
} from '@/components/ui';

interface ScrapedProperty {
  apn: string;
  address: string;
  city: string;
  county: string;
  state: string;
  zip?: string;
  owner_name?: string;
  land_area?: number;
  building_area?: number;
  year_built?: number;
  property_class?: string;
  assessed_value?: number;
  latitude?: number;
  longitude?: number;
}

interface ScrapeResult {
  success: boolean;
  source: string;
  properties_found: number;
  properties_returned: number;
  duration_ms: number;
  properties: ScrapedProperty[];
}

interface Preset {
  id: string;
  name: string;
  sources: Array<{ state: string; county: string }>;
  source_count: number;
}

const US_STATES = [
  { value: 'TX', label: 'Texas' },
  { value: 'CA', label: 'California' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'FL', label: 'Florida' },
  { value: 'NV', label: 'Nevada' },
  { value: 'CO', label: 'Colorado' },
];

const TEXAS_COUNTIES = [
  { value: 'Travis', label: 'Travis (Austin)' },
  { value: 'Harris', label: 'Harris (Houston)' },
  { value: 'Dallas', label: 'Dallas' },
  { value: 'Bexar', label: 'Bexar (San Antonio)' },
  { value: 'Tarrant', label: 'Tarrant (Fort Worth)' },
];

const CALIFORNIA_COUNTIES = [
  { value: 'Los Angeles', label: 'Los Angeles' },
  { value: 'San Diego', label: 'San Diego' },
  { value: 'Orange', label: 'Orange' },
  { value: 'San Francisco', label: 'San Francisco' },
  { value: 'Santa Clara', label: 'Santa Clara' },
];

export default function ScrapePage() {
  const [selectedState, setSelectedState] = useState<string>('TX');
  const [selectedCounty, setSelectedCounty] = useState<string>('Travis');
  const [query, setQuery] = useState<string>('residential');
  const [limit, setLimit] = useState<number>(20);
  const [scrapeResult, setScrapeResult] = useState<ScrapeResult | null>(null);
  const [selectedProperties, setSelectedProperties] = useState<Set<string>>(new Set());

  const { data: presets } = useQuery<Preset[]>({
    queryKey: ['presets'],
    queryFn: async () => {
      const response = await fetch('https://prisai-api.cyopsys.workers.dev/api/presets', {
        headers: { 'X-API-Key': 'pra_idbuCt7x7GQju0tRfqAw' },
      });
      return response.json();
    },
  });

  const scrapeMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch('https://prisai-api.cyopsys.workers.dev/api/scrape', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': 'pra_idbuCt7x7GQju0tRfqAw',
        },
        body: JSON.stringify({ state: selectedState, county: selectedCounty, query, limit }),
      });
      return response.json() as Promise<ScrapeResult>;
    },
    onSuccess: (data) => {
      setScrapeResult(data);
      setSelectedProperties(new Set());
    },
  });

  const ingestMutation = useMutation({
    mutationFn: async (properties: ScrapedProperty[]) => {
      for (const prop of properties) {
        await fetch('https://prisai-api.cyopsys.workers.dev/api/parcels', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': 'pra_idbuCt7x7GQju0tRfqAw',
          },
          body: JSON.stringify(prop),
        });
      }
    },
    onSuccess: () => {
      alert('Properties ingested successfully!');
    },
  });

  const counties = selectedState === 'TX' ? TEXAS_COUNTIES : 
                   selectedState === 'CA' ? CALIFORNIA_COUNTIES : [];

  const toggleProperty = (apn: string) => {
    const newSelected = new Set(selectedProperties);
    if (newSelected.has(apn)) {
      newSelected.delete(apn);
    } else {
      newSelected.add(apn);
    }
    setSelectedProperties(newSelected);
  };

  const selectAll = () => {
    if (scrapeResult) {
      setSelectedProperties(new Set(scrapeResult.properties.map(p => p.apn)));
    }
  };

  const deselectAll = () => {
    setSelectedProperties(new Set());
  };

  const handleIngest = () => {
    if (!scrapeResult) return;
    const toIngest = scrapeResult.properties.filter(p => selectedProperties.has(p.apn));
    if (toIngest.length > 0) {
      ingestMutation.mutate(toIngest);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Property Scraper</h1>
        <p className="text-gray-600">Collect property records from county assessor databases</p>
      </div>

      {/* Scraper Config */}
      <Card>
        <CardHeader>
          <h3 className="font-semibold">Configure Scraping Job</h3>
        </CardHeader>
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
              <select
                value={selectedState}
                onChange={(e) => {
                  setSelectedState(e.target.value);
                  const firstCounty = e.target.value === 'TX' ? 'Travis' : 
                                    e.target.value === 'CA' ? 'Los Angeles' : '';
                  setSelectedCounty(firstCounty);
                }}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              >
                {US_STATES.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">County</label>
              <select
                value={selectedCounty}
                onChange={(e) => setSelectedCounty(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              >
                {counties.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Query</label>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="e.g., residential, commercial"
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Results</label>
              <input
                type="number"
                value={limit}
                onChange={(e) => setLimit(parseInt(e.target.value) || 20)}
                min={1}
                max={100}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="mt-4 flex gap-4">
            <Button onClick={() => scrapeMutation.mutate()} isLoading={scrapeMutation.isPending}>
              {scrapeMutation.isPending ? 'Scraping...' : 'Start Scrape'}
            </Button>
            {scrapeResult && (
              <Button variant="outline" onClick={() => setScrapeResult(null)}>
                Clear Results
              </Button>
            )}
          </div>
        </CardBody>
      </Card>

      {/* Presets */}
      {presets && presets.length > 0 && (
        <Card>
          <CardHeader>
            <h3 className="font-semibold">Ingestion Presets</h3>
          </CardHeader>
          <CardBody>
            <div className="flex flex-wrap gap-2">
              {presets.map((preset) => (
                <button
                  key={preset.id}
                  className="px-3 py-1 rounded-full border border-gray-300 text-sm hover:bg-gray-50"
                >
                  {preset.name} ({preset.source_count} counties)
                </button>
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Results */}
      {scrapeMutation.isError && (
        <Alert variant="error" title="Scrape Failed">
          {scrapeMutation.error instanceof Error ? scrapeMutation.error.message : 'Unknown error'}
        </Alert>
      )}

      {scrapeResult && (
        <>
          <Card>
            <CardHeader className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold">Scraped Properties</h3>
                <p className="text-sm text-gray-500">
                  {scrapeResult.source} • {scrapeResult.properties_returned} of {scrapeResult.properties_found} returned • {scrapeResult.duration_ms}ms
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={selectAll}>Select All</Button>
                <Button variant="outline" size="sm" onClick={deselectAll}>Deselect All</Button>
              </div>
            </CardHeader>
            <CardBody className="p-0">
              {scrapeResult.properties.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-8">&nbsp;</TableHead>
                      <TableHead>Address</TableHead>
                      <TableHead>Owner</TableHead>
                      <TableHead>APN</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Value</TableHead>
                      <TableHead>Year</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {scrapeResult.properties.map((property) => (
                      <TableRow key={property.apn} className={selectedProperties.has(property.apn) ? 'bg-blue-50' : ''}>
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={selectedProperties.has(property.apn)}
                            onChange={() => toggleProperty(property.apn)}
                            className="rounded border-gray-400"
                          />
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">{property.address}</div>
                            <div className="text-sm text-gray-500">
                              {property.city}, {property.state} {property.zip}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{property.owner_name || '-'}</TableCell>
                        <TableCell className="font-mono text-xs">{property.apn}</TableCell>
                        <TableCell>
                          <Badge variant="default">{property.property_class || 'Residential'}</Badge>
                        </TableCell>
                        <TableCell>
                          {property.assessed_value 
                            ? `$${property.assessed_value.toLocaleString()}`
                            : '-'}
                        </TableCell>
                        <TableCell>{property.year_built || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <EmptyState icon="📭" title="No properties found" description="Try a different query" />
              )}
            </CardBody>
          </Card>

          {/* Ingest Actions */}
          {selectedProperties.size > 0 && (
            <Card>
              <CardBody>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">
                    {selectedProperties.size} properties selected
                  </span>
                  <Button onClick={handleIngest} isLoading={ingestMutation.isPending}>
                    Ingest Selected to Database
                  </Button>
                </div>
              </CardBody>
            </Card>
          )}
        </>
      )}

      {/* Info */}
      <Card>
        <CardHeader>
          <h3 className="font-semibold">How Scraping Works</h3>
        </CardHeader>
        <CardBody>
          <ol className="list-decimal list-inside space-y-2 text-sm text-gray-600">
            <li>Select a state and county from the dropdown</li>
            <li>Enter a search query (e.g., "residential", "commercial")</li>
            <li>Click "Start Scrape" to collect properties</li>
            <li>Select the properties you want to import</li>
            <li>Click "Ingest Selected" to add them to the database</li>
          </ol>
          <div className="mt-4 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> This scraper uses simulated data based on public records patterns. 
              In production, it would connect to actual county assessor APIs.
            </p>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
